import type {
  ConsumptionMethodRecord, EstimateConfidence, PurchaseTemplateRecord,
} from '../core/types.js';
import type { NormalizedUsage } from '../rules/usage-normalizer.js';
import type { ResolvedPurchase } from './purchases.js';

export type SavingsAlgo = 'unit_avoidance' | 'purchase_cadence' | 'period_spend' | 'legacy_money_model';

export interface SavingsBreakdownItem {
  templateId?: string | undefined;
  methodId?: string | undefined;
  label: string;
  mode: SavingsAlgo;
  actualSpend: number;
  expectedBaselineSpend: number;
  estimatedSavings: number;
  spendAboveBaseline: number;
  confidence: EstimateConfidence;
  explanation: string;
}

export interface SavingsSummary {
  habitId: string;
  currency: string;
  periodStart: string;
  periodEnd: string;
  actualSpend: number;
  expectedBaselineSpend: number;
  estimatedSavings: number;
  spendAboveBaseline: number;
  confidence: EstimateConfidence;
  explanation: string;
  breakdown: SavingsBreakdownItem[];
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function elapsedDays(periodStart: string, periodEnd: string): number {
  const ms = new Date(periodEnd).getTime() - new Date(periodStart).getTime();
  return Math.max(0, ms / MS_PER_DAY);
}

function clamp0(n: number): number { return n < 0 ? 0 : n; }

function periodToDays(period: 'day' | 'week' | 'month'): number {
  if (period === 'day') return 1;
  if (period === 'week') return 7;
  return 30;
}

function summarizeConfidence(items: SavingsBreakdownItem[]): EstimateConfidence {
  if (items.some((i) => i.confidence === 'low')) return 'low';
  if (items.every((i) => i.confidence === 'high')) return 'high';
  return 'medium';
}

export interface UnitAvoidanceInput {
  template: PurchaseTemplateRecord;
  method: ConsumptionMethodRecord | undefined;
  baselineUnitsPerDay: number;
  costPerUnit: number;
  actualUnits: number;
  actualSpend: number;
  periodStart: string;
  periodEnd: string;
}

export function unitAvoidance(input: UnitAvoidanceInput): SavingsBreakdownItem {
  const days = elapsedDays(input.periodStart, input.periodEnd);
  const expectedUnits = input.baselineUnitsPerDay * days;
  const expectedBaselineSpend = expectedUnits * input.costPerUnit;
  const actualSpend = input.actualSpend;
  const avoidedUnits = clamp0(expectedUnits - input.actualUnits);
  const estimatedSavings = avoidedUnits * input.costPerUnit;
  const spendAboveBaseline = clamp0(actualSpend - expectedBaselineSpend);
  const confidence: EstimateConfidence = input.template.confidence;
  return {
    templateId: input.template.id,
    methodId: input.method?.id,
    label: input.template.label,
    mode: 'unit_avoidance',
    actualSpend,
    expectedBaselineSpend,
    estimatedSavings,
    spendAboveBaseline,
    confidence,
    explanation: `Avoided ${avoidedUnits.toFixed(0)} ${input.template.packageUnitLabel ?? 'units'} versus old pace of ${input.baselineUnitsPerDay}/day.`,
  };
}

export interface PurchaseCadenceInput {
  template: PurchaseTemplateRecord;
  method: ConsumptionMethodRecord | undefined;
  typicalPurchasePrice: number;
  baselineDaysPerPurchase: number;
  actualSpend: number;
  periodStart: string;
  periodEnd: string;
}

export function purchaseCadence(input: PurchaseCadenceInput): SavingsBreakdownItem {
  const days = elapsedDays(input.periodStart, input.periodEnd);
  const baselineSpendPerDay = input.baselineDaysPerPurchase > 0
    ? input.typicalPurchasePrice / input.baselineDaysPerPurchase
    : 0;
  const expectedBaselineSpend = baselineSpendPerDay * days;
  const estimatedSavings = clamp0(expectedBaselineSpend - input.actualSpend);
  const spendAboveBaseline = clamp0(input.actualSpend - expectedBaselineSpend);
  return {
    templateId: input.template.id,
    methodId: input.method?.id,
    label: input.template.label,
    mode: 'purchase_cadence',
    actualSpend: input.actualSpend,
    expectedBaselineSpend,
    estimatedSavings,
    spendAboveBaseline,
    confidence: input.template.confidence,
    explanation: `Old pace: 1 ${input.template.label} every ${input.baselineDaysPerPurchase} days at ${input.template.currency} ${input.typicalPurchasePrice.toFixed(2)}.`,
  };
}

export interface PeriodSpendInput {
  template: PurchaseTemplateRecord;
  method: ConsumptionMethodRecord | undefined;
  baselineAmount: number;
  baselinePeriod: 'day' | 'week' | 'month';
  actualSpend: number;
  periodStart: string;
  periodEnd: string;
}

export function periodSpend(input: PeriodSpendInput): SavingsBreakdownItem {
  const days = elapsedDays(input.periodStart, input.periodEnd);
  const baselineDays = periodToDays(input.baselinePeriod);
  const expectedBaselineSpend = (input.baselineAmount / baselineDays) * days;
  const estimatedSavings = clamp0(expectedBaselineSpend - input.actualSpend);
  const spendAboveBaseline = clamp0(input.actualSpend - expectedBaselineSpend);
  return {
    templateId: input.template.id,
    methodId: input.method?.id,
    label: input.template.label,
    mode: 'period_spend',
    actualSpend: input.actualSpend,
    expectedBaselineSpend,
    estimatedSavings,
    spendAboveBaseline,
    confidence: input.template.confidence,
    explanation: `Baseline ${input.template.currency} ${input.baselineAmount.toFixed(2)}/${input.baselinePeriod}.`,
  };
}

export interface ComputeSummaryInput {
  habitId: string;
  currency: string;
  periodStart: string;
  periodEnd: string;
  templates: PurchaseTemplateRecord[];
  methods: ConsumptionMethodRecord[];
  purchases: ResolvedPurchase[];
  usage: NormalizedUsage[];
}

/**
 * Compose a SavingsSummary by applying the configured savings mode for each
 * active template owned by the habit. Templates with no usable baseline are
 * skipped silently — they still surface their actualSpend via their breakdown.
 */
export function computeSavingsSummary(input: ComputeSummaryInput): SavingsSummary {
  const breakdown: SavingsBreakdownItem[] = [];

  for (const template of input.templates) {
    if (!template.active) continue;
    if (template.habitId !== input.habitId) continue;
    const method = template.methodId
      ? input.methods.find((m) => m.id === template.methodId)
      : undefined;

    const matchingPurchases = input.purchases.filter((p) => {
      if (p.habitId !== input.habitId) return false;
      if (p.occurredAt < input.periodStart || p.occurredAt > input.periodEnd) return false;
      if (template.methodId) return p.methodId === template.methodId || p.templateId === template.id;
      return p.templateId === template.id || !p.templateId;
    });
    const actualSpend = matchingPurchases.reduce((acc, p) => acc + p.amount, 0);

    if (template.savingsMode === 'unit_avoidance' && template.packageQuantity && template.defaultPrice) {
      const costPerUnit = template.defaultPrice / template.packageQuantity;
      const usageUnits = input.usage
        .filter((u) => (method ? u.methodId === method.id : true))
        .reduce((acc, u) => acc + u.domainUnits, 0);
      const baselineUnitsPerDay = template.baselineSpendPerPeriod
        ? (template.baselineSpendPerPeriod.amount / costPerUnit) / periodToDays(template.baselineSpendPerPeriod.period)
        : 0;
      if (baselineUnitsPerDay > 0) {
        breakdown.push(unitAvoidance({
          template, method,
          baselineUnitsPerDay,
          costPerUnit,
          actualUnits: usageUnits,
          actualSpend,
          periodStart: input.periodStart,
          periodEnd: input.periodEnd,
        }));
        continue;
      }
    }

    if (template.savingsMode === 'purchase_cadence'
      && template.defaultPrice
      && template.baselineDaysPerPurchase
      && template.baselineDaysPerPurchase > 0) {
      breakdown.push(purchaseCadence({
        template, method,
        typicalPurchasePrice: template.defaultPrice,
        baselineDaysPerPurchase: template.baselineDaysPerPurchase,
        actualSpend,
        periodStart: input.periodStart,
        periodEnd: input.periodEnd,
      }));
      continue;
    }

    if (template.savingsMode === 'period_spend' && template.baselineSpendPerPeriod) {
      breakdown.push(periodSpend({
        template, method,
        baselineAmount: template.baselineSpendPerPeriod.amount,
        baselinePeriod: template.baselineSpendPerPeriod.period,
        actualSpend,
        periodStart: input.periodStart,
        periodEnd: input.periodEnd,
      }));
      continue;
    }

    // No usable baseline: still surface actual spend, no savings claim.
    breakdown.push({
      templateId: template.id,
      methodId: method?.id,
      label: template.label,
      mode: template.savingsMode === 'unit_avoidance'
        ? 'unit_avoidance'
        : template.savingsMode === 'purchase_cadence'
          ? 'purchase_cadence'
          : 'period_spend',
      actualSpend,
      expectedBaselineSpend: 0,
      estimatedSavings: 0,
      spendAboveBaseline: 0,
      confidence: 'low',
      explanation: 'Add your old buying pace to estimate savings.',
    });
  }

  const totalActual = breakdown.reduce((a, b) => a + b.actualSpend, 0);
  const totalExpected = breakdown.reduce((a, b) => a + b.expectedBaselineSpend, 0);
  const totalSavings = breakdown.reduce((a, b) => a + b.estimatedSavings, 0);
  const totalAbove = breakdown.reduce((a, b) => a + b.spendAboveBaseline, 0);

  return {
    habitId: input.habitId,
    currency: input.currency,
    periodStart: input.periodStart,
    periodEnd: input.periodEnd,
    actualSpend: totalActual,
    expectedBaselineSpend: totalExpected,
    estimatedSavings: totalSavings,
    spendAboveBaseline: totalAbove,
    confidence: breakdown.length === 0 ? 'low' : summarizeConfidence(breakdown),
    explanation: breakdown.length === 0
      ? 'No purchase templates configured.'
      : 'Combined estimate from active purchase templates.',
    breakdown,
  };
}
